import { Button, Flex, Tooltip, Typography } from '@aloft/toolkit-sui';

import { useAppraisifyNavigation } from '../../../hooks/validation/useAppraisifyNavigation.ts';
import { selectIsCommentaryLoaded } from '../../../redux/features/selectors/commentarySelectors/selectIsCommentaryLoaded.ts';
import {
  selectIncludeCostApproach,
  selectIsMockOrder,
  selectOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { getConfig } from '../../../utils/getConfig.ts';
import { useCertifyModal } from '../../CertifyModal/hooks/useCertifyModal.tsx';
import { useExportModal } from '../../ExportModal/useExportModal.tsx';
import { GuidedTourTooltip } from '../../GuidedTour/GuidedTourTooltip.tsx';
import { SUBNAV_HEIGHT } from '../consts.ts';
import { HeaderSubNavItem } from './HeaderSubNavItem.tsx';
import {
  APPRAISIFY_ONLY_ROUTES,
  INSPECTIFY_SUBNAV_GROUPS,
  SAAS_ONLY_ROUTES,
  SAAS_SUBNAV_GROUPS,
} from './constants.ts';
import { NavGroup, SubNavItemType, groupNavItems } from './types.ts';
import { useActiveSubNav } from './useActiveSubNav.ts';

const getGroupTitleColor = (label: string) => {
  switch (label) {
    case 'SALES':
      return '#0072b2';
    case 'RENTALS':
      return '#e79f00';
    default:
      return '#9ca6af';
  }
};

type DesktopSubNavRoute = (typeof INSPECTIFY_SUBNAV_GROUPS)[number]['routes'][number];

const desktopSubNavRoutes = new Set<DesktopSubNavRoute>([
  ...INSPECTIFY_SUBNAV_GROUPS.flatMap((group) => group.routes),
  ...SAAS_SUBNAV_GROUPS.flatMap((group) => group.routes),
]);
const costApproachRoutes = new Set(['cost-approach', 'craftsman-cost-approach']);

const isDesktopSubNavItem = (
  item: SubNavItemType,
): item is SubNavItemType & { route: DesktopSubNavRoute } =>
  desktopSubNavRoutes.has(item.route as DesktopSubNavRoute);

export const HeaderDesktopSubNav = () => {
  const { canCertify, getCertifyErrorText } = useAppraisifyNavigation();
  const subnav = useActiveSubNav();
  const isCommentaryLoaded = useAppSelector((state) => selectIsCommentaryLoaded(state));
  const { modal, openModal } = useExportModal(isCommentaryLoaded);
  const { certifyModal, openCertifyModal } = useCertifyModal();
  const order = useAppSelector(selectOrder);
  const isMock = useAppSelector(selectIsMockOrder);
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);

  if (!subnav) return null;
  const certifyErrorText = getCertifyErrorText();
  const visibleSubnav = subnav
    .filter((item) => {
      if (APPRAISIFY_ONLY_ROUTES.includes(item.route) && !order.isInspectifyOrder) {
        return false;
      }
      if (SAAS_ONLY_ROUTES.includes(item.route) && order.isInspectifyOrder) {
        return false;
      }
      if (!includeCostApproach && costApproachRoutes.has(item.route)) {
        return false;
      }
      return true;
    })
    .filter(isDesktopSubNavItem);

  let subnavGroups: NavGroup<DesktopSubNavRoute>[] = order.isInspectifyOrder
    ? INSPECTIFY_SUBNAV_GROUPS
    : SAAS_SUBNAV_GROUPS;

  if (!order.hasRentalConfiguration) {
    subnavGroups = subnavGroups.filter((group) => group.label !== 'RENTALS');
  }

  const groupedSubnav = groupNavItems(
    visibleSubnav as (SubNavItemType & { route: DesktopSubNavRoute })[],
    subnavGroups,
  );

  const handleDocumentsClick = () => {
    if (!order.isInspectifyOrder || !order.orderNumber) return;

    const url = `${String(getConfig('INSPECTIFY_MARKETPLACE_BASE_URL'))}/orders/${order.orderNumber}/result`;

    window.open(url, '_blank');
    window.focus();
  };

  return (
    <Flex
      className="main-header-desktop-subnav"
      position="absolute"
      alignItems="stretch"
      top="3rem"
      height={SUBNAV_HEIGHT}
      width="calc(100vw + 3rem + 8px)"
      marginLeft="calc(-3rem - 8px)"
      paddingLeft="3rem"
      zIndex={9}
      backgroundColor="surface1"
    >
      <Flex className="main-header-desktop-subnav-groups" alignItems="center" height="100%">
        {groupedSubnav.map((group, groupIndex) => (
          <Flex
            className="main-header-desktop-subnav-group"
            key={`header-subnav-group-${group.label}`}
            alignItems="center"
            paddingRight={groupIndex < groupedSubnav.length - 1 ? '0.2rem' : '0'}
          >
            <Flex
              className={[
                'main-header-desktop-subnav-group-shell',
                groupIndex === 0 ? 'main-header-desktop-subnav-group-shell-first' : '',
              ].join(' ')}
            >
              <Flex className="main-header-desktop-subnav-group-title-wrap">
                <Typography
                  variant="subtitle2"
                  __dangerDoNotUseSx={{
                    textTransform: 'uppercase',
                    color: getGroupTitleColor(group.label),
                  }}
                >
                  {group.label}
                </Typography>
              </Flex>
              <Flex alignItems="center">
                {group.items.map((item) => (
                  <HeaderSubNavItem key={item.route} item={item} />
                ))}
              </Flex>
            </Flex>
          </Flex>
        ))}
      </Flex>
      {isCommentaryLoaded && (
        <Flex className="main-header-desktop-subnav-actions">
          {order.isInspectifyOrder ? (
            <>
              <Tooltip label={certifyErrorText ?? 'Certify the report'}>
                <Button
                  text={isMock ? 'Complete' : 'Certify'}
                  disabled={!canCertify}
                  onClick={openCertifyModal}
                  size="sm"
                  __testId="craftsman-certify-button"
                />
              </Tooltip>
              <Button
                text="Documents"
                size="sm"
                variant="secondary"
                onClick={handleDocumentsClick}
              />
            </>
          ) : (
            <GuidedTourTooltip
              text="Click 'Export' to export PDFs of your analysis or a file which can be imported directly into your form filling software."
              tourIndex={11}
              tourID="analysisTour"
            >
              <Button
                text="Export"
                onClick={openModal}
                size="sm"
                __testId="craftsman-export-button"
              />
            </GuidedTourTooltip>
          )}
        </Flex>
      )}
      {certifyModal}
      {modal}
    </Flex>
  );
};
